/*
 * Our Story collage parallax.
 * Each collage image declares its own data-depth (px). As the collage scrolls
 * through the viewport, every image is translated vertically by a fraction of
 * its depth, so images on different "grounds" drift at different rates. A single
 * rAF loop drives all items; runs only while the collage is on screen and
 * respects reduced-motion.
 */
class FluevogOurStory extends HTMLElement {
  connectedCallback() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.enabled = this.dataset.parallax === 'true' && !this.reducedMotion;
    this.items = Array.from(this.querySelectorAll('[data-depth]'));

    if (!this.enabled || this.items.length === 0) return;

    // Soften large depths on mobile so the collage doesn't feel floaty/janky.
    this.depthScale = window.matchMedia('(pointer: coarse), (max-width: 749px)').matches
      ? 0.55
      : 1;

    // Preserve any authored rotation so the transform doesn't clobber it.
    this.items.forEach((item) => {
      const rotate = getComputedStyle(item).getPropertyValue('--fv-item-rotate').trim() || '0deg';
      item.dataset.rotate = rotate;
    });

    this.visible = false;
    this.ticking = false;
    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);

    this.observer = new IntersectionObserver(
      (entries) => {
        this.visible = entries.some((e) => e.isIntersecting);
        if (this.visible) this.onScroll();
      },
      { rootMargin: '15% 0px' }
    );
    this.observer.observe(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.observer?.disconnect();
  }

  onScroll() {
    if (this.ticking || !this.visible) return;
    this.ticking = true;
    requestAnimationFrame(this.update);
  }

  update() {
    this.ticking = false;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const rect = this.getBoundingClientRect();

    // progress: -1 entering at the bottom, 0 centered, +1 leaving at the top.
    const center = rect.top + rect.height / 2;
    const progress = (center - vh / 2) / ((vh + rect.height) / 2);
    const clamped = Math.max(-1, Math.min(1, progress));

    this.items.forEach((item) => {
      const depth = parseFloat(item.dataset.depth || '0') * this.depthScale;
      const shift = (-clamped * depth).toFixed(2);
      item.style.transform = `translate3d(0, ${shift}px, 0) rotate(${item.dataset.rotate})`;
    });
  }
}

if (!customElements.get('fv-our-story')) {
  customElements.define('fv-our-story', FluevogOurStory);
}
